// ---------------------------------------------------------------------------
// STATE PAGE HELPERS — job alerts + "Top cities" aggregate.
//
// Alerts reuse the ONE job-alert system (mkt_saved_searches + the daily
// job-alerts-digest cron):
//   * signed-in  -> createSavedSearch() directly (RLS, same as the dashboard)
//   * signed-out -> edge function `state-job-alert`, which writes the same
//                   table with user_id NULL and active=false, then emails a
//                   double opt-in link. The digest only mails active rows.
// ---------------------------------------------------------------------------
import { supabase } from '@/lib/supabase';
import type { Lang } from '@/data/constants';
import { createSavedSearch, listSavedSearches, MAX_SAVED_SEARCHES } from '@/lib/savedSearches';

export type StateAlertStatus = 'active' | 'already_active' | 'pending' | 'too_many' | 'rate_limited' | 'error';

export const createStateAlert = async (opts: {
  profile?: { id: string; email: string } | null;
  email: string;
  lang: Lang;
  state: string;
  stateName: string;
  /** Optional city (state+city landing pages). Matched exactly by the digest. */
  city?: string | null;
  trades: string[];
}): Promise<StateAlertStatus> => {
  const trades = [...new Set(opts.trades)].sort();
  const state = opts.state.toUpperCase();
  const city = (opts.city || '').trim() || null;
  const sameCity = (c?: string | null) => (c || '').trim().toLowerCase() === (city || '').toLowerCase();

  // Signed in AND submitting their own account email -> real saved search now.
  if (opts.profile?.id && opts.profile.email?.toLowerCase() === opts.email.toLowerCase()) {
    const mine = await listSavedSearches(opts.profile.id);
    const dup = mine.find(
      (s) => s.state === state && sameCity(s.city) && JSON.stringify([...(s.trades || [])].sort()) === JSON.stringify(trades),
    );
    if (dup) return 'already_active';
    if (mine.length >= MAX_SAVED_SEARCHES) return 'too_many';
    const err = await createSavedSearch(opts.profile.id, opts.profile.email, opts.lang, {
      name: `${city ? `${city}, ${state}` : opts.stateName} projects`.slice(0, 80),
      trades,
      state,
      city,
      project_type: null,
      min_budget: null,
      frequency: 'daily',
    });
    return err ? 'error' : 'active';
  }

  const { data, error } = await supabase.functions.invoke('state-job-alert', {
    body: {
      email: opts.email,
      lang: opts.lang,
      state,
      city: city || undefined,
      trades,
      pageUrl: typeof window !== 'undefined' ? window.location.href.slice(0, 300) : undefined,
    },
  });
  if (error || !data) return 'error';
  if (data.error === 'too_many_alerts') return 'too_many';
  if (data.error === 'rate_limited') return 'rate_limited';
  if (data.status === 'already_active') return 'already_active';
  if (data.status === 'pending') return 'pending';
  return 'error';
};

export interface CityCount { city: string; count: number }
export interface TopCities { totalOpen: number; citiesWithProjects: number; cities: CityCount[]; generatedAt: string }

/** Server-side aggregate (browse-listings `cities` mode): names + counts only. */
export const fetchTopCities = async (state: string, limit = 10): Promise<TopCities | null> => {
  const { data, error } = await supabase.functions.invoke('browse-listings', {
    body: { cities: true, state: state.toUpperCase(), limit },
  });
  if (error || !data || data.error || !Array.isArray(data.cities)) return null;
  return {
    totalOpen: Number(data.totalOpen || 0),
    citiesWithProjects: Number(data.citiesWithProjects || 0),
    cities: data.cities as CityCount[],
    generatedAt: String(data.generatedAt || new Date().toISOString()),
  };
};

/**
 * Every state's ranked top cities in ONE call (browse-listings `cityIndex`
 * aggregate — names + counts only, same ranking as `cities` mode). Requested
 * with perState = CITY_PAGES_PER_STATE so it shares the server's 60s cache
 * entry with the sitemap. Memoised per tab for 60s so navigating between city
 * money pages never re-scans.
 */
let indexMemo: { at: number; perState: number; p: Promise<Record<string, CityCount[]> | null> } | null = null;

export const fetchCityIndex = (perState: number): Promise<Record<string, CityCount[]> | null> => {
  const now = Date.now();
  if (indexMemo && indexMemo.perState === perState && now - indexMemo.at < 60_000) return indexMemo.p;
  const p = supabase.functions
    .invoke('browse-listings', { body: { cityIndex: true, perState } })
    .then(({ data, error }) => {
      if (error || !data || data.error || !data.states || typeof data.states !== 'object') return null;
      return data.states as Record<string, CityCount[]>;
    })
    .catch(() => null);
  indexMemo = { at: now, perState, p };
  // A failed lookup must not be cached — the next page view retries.
  p.then((v) => {
    if (v === null && indexMemo?.p === p) indexMemo = null;
  });
  return p;
};
